export function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

export function isValidCpf(value: string) {
  const cpf = onlyDigits(value);

  if (cpf.length !== 11) {
    return false;
  }

  if (new Set(cpf).size === 1) {
    return false;
  }

  const firstTotal = cpf
    .slice(0, 9)
    .split("")
    .reduce(
      (sum, digit, index) =>
        sum + Number(digit) * (10 - index),
      0,
    );

  let firstDigit = (firstTotal * 10) % 11;

  if (firstDigit === 10) {
    firstDigit = 0;
  }

  if (firstDigit !== Number(cpf[9])) {
    return false;
  }

  const secondTotal = cpf
    .slice(0, 10)
    .split("")
    .reduce(
      (sum, digit, index) =>
        sum + Number(digit) * (11 - index),
      0,
    );

  let secondDigit = (secondTotal * 10) % 11;

  if (secondDigit === 10) {
    secondDigit = 0;
  }

  return secondDigit === Number(cpf[10]);
}

export function formatCpf(value: string) {
  const digits = onlyDigits(value).slice(0, 11);

  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

export function normalizeCnpj(value: string) {
  return value
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, 14);
}

export function isValidCnpj(value: string) {
  const cnpj = normalizeCnpj(value);

  if (cnpj.length !== 14) {
    return false;
  }

  const base = cnpj.slice(0, 12);
  const checkDigits = cnpj.slice(12);

  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj)) {
    return false;
  }

  if (new Set(base).size === 1) {
    return false;
  }

  function characterValue(char: string) {
    return char.charCodeAt(0) - 48;
  }

  function calculateDigit(
    chars: string,
    weights: number[],
  ) {
    const total = chars
      .split("")
      .reduce(
        (sum, char, index) =>
          sum +
          characterValue(char) *
            weights[index],
        0,
      );

    const remainder = total % 11;

    if (remainder === 0 || remainder === 1) {
      return "0";
    }

    return String(11 - remainder);
  }

  const firstDigit = calculateDigit(
    base,
    [
      5, 4, 3, 2,
      9, 8, 7, 6,
      5, 4, 3, 2,
    ],
  );

  const secondDigit = calculateDigit(
    base + firstDigit,
    [
      6, 5, 4, 3, 2,
      9, 8, 7, 6,
      5, 4, 3, 2,
    ],
  );

  return (
    checkDigits ===
    firstDigit + secondDigit
  );
}

export function formatCnpj(value: string) {
  const normalized = normalizeCnpj(value);

  if (normalized.length <= 2) {
    return normalized;
  }

  if (normalized.length <= 5) {
    return `${normalized.slice(0, 2)}.${normalized.slice(2)}`;
  }

  if (normalized.length <= 8) {
    return (
      `${normalized.slice(0, 2)}.` +
      `${normalized.slice(2, 5)}.` +
      normalized.slice(5)
    );
  }

  if (normalized.length <= 12) {
    return (
      `${normalized.slice(0, 2)}.` +
      `${normalized.slice(2, 5)}.` +
      `${normalized.slice(5, 8)}/` +
      normalized.slice(8)
    );
  }

  return (
    `${normalized.slice(0, 2)}.` +
    `${normalized.slice(2, 5)}.` +
    `${normalized.slice(5, 8)}/` +
    `${normalized.slice(8, 12)}-` +
    normalized.slice(12, 14)
  );
}

export function formatCep(value: string) {
  const digits = onlyDigits(value).slice(0, 8);

  if (digits.length <= 5) {
    return digits;
  }

  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function formatBrazilPhone(value: string) {
  let digits = onlyDigits(value).slice(0, 13);

  if (digits.startsWith("55")) {
    digits = digits.slice(2);
  }

  digits = digits.slice(0, 11);

  if (digits.length === 0) {
    return "";
  }

  if (digits.length <= 2) {
    return `+55 (${digits}`;
  }

  const ddd = digits.slice(0, 2);
  const number = digits.slice(2);

  if (number.length <= 4) {
    return `+55 (${ddd}) ${number}`;
  }

  if (number.length <= 8) {
    return `+55 (${ddd}) ${number.slice(0, 4)}-${number.slice(4)}`;
  }

  return `+55 (${ddd}) ${number.slice(0, 5)}-${number.slice(5)}`;
}
